import _extends from "@babel/runtime/helpers/extends";
import { useEffect, useRef, useState } from "react";
import makeCancelable from "./make-cancelable";
import { toString } from "../../utils/deterministic-string";
import { CACHE_TYPES, STATUSES } from "./fetch-utils";
import * as singlePromiseCache from "./single-promise-cache";

var retrieveCache = function retrieveCache(_ref) {
  var cacheType = _ref.cacheType,
      request = _ref.request,
      using = _ref.using;

  if (cacheType === CACHE_TYPES.NO_CACHE) {
    return undefined;
  }

  if (cacheType === CACHE_TYPES.SINGLE) {
    return singlePromiseCache.retrieve({
      request: request,
      using: using
    });
  }

  return undefined;
};

var storeCache = function storeCache(_ref2) {
  var cacheType = _ref2.cacheType,
      promise = _ref2.promise,
      request = _ref2.request,
      using = _ref2.using;

  if (cacheType === CACHE_TYPES.NO_CACHE) {
    return;
  }

  if (cacheType === CACHE_TYPES.SINGLE) {
    singlePromiseCache.store({
      promise: promise,
      request: request,
      using: using
    });
  }
};

var fetchAndCache = function fetchAndCache(_ref3) {
  var cacheType = _ref3.cacheType,
      request = _ref3.request,
      using = _ref3.using;
  var promise = request(using);
  storeCache({
    cacheType: cacheType,
    promise: promise,
    request: request,
    using: using
  });
  return promise;
};

var errorToString = function errorToString(e) {
  return typeof e === "object" ? e.message || "" : e || "";
};

var wrapPromise = function wrapPromise(rawPromise, setState) {
  var _makeCancelable = makeCancelable(rawPromise),
      promise = _makeCancelable.promise,
      cancel = _makeCancelable.cancel;

  promise.then(function (data) {
    setState({
      data: data,
      dataMatchesRequest: true,
      error: undefined,
      status: STATUSES.DONE
    });
  }).catch(function (e) {
    if (typeof e === "object" && e.isCanceled) {
      return;
    }

    setState({
      data: undefined,
      dataMatchesRequest: false,
      error: errorToString(e),
      status: STATUSES.ERROR
    });
  });
  return cancel;
};

var defaultState = {
  status: STATUSES.INITIAL,
  dataMatchesRequest: false
};

var useFetch = function useFetch(_ref4) {
  var _ref4$cacheAndNetwork = _ref4.cacheAndNetwork,
      cacheAndNetwork = _ref4$cacheAndNetwork === void 0 ? false : _ref4$cacheAndNetwork,
      _ref4$cacheType = _ref4.cacheType,
      cacheType = _ref4$cacheType === void 0 ? CACHE_TYPES.NO_CACHE : _ref4$cacheType,
      _ref4$invalidatorTime = _ref4.invalidatorTime,
      invalidatorTime = _ref4$invalidatorTime === void 0 ? null : _ref4$invalidatorTime,
      _ref4$request = _ref4.request,
      request = _ref4$request === void 0 ? function () {
    return Promise.resolve();
  } : _ref4$request,
      _ref4$skipCall = _ref4.skipCall,
      skipCall = _ref4$skipCall === void 0 ? false : _ref4$skipCall,
      _ref4$using = _ref4.using,
      using = _ref4$using === void 0 ? {} : _ref4$using,
      _ref4$willUpdate = _ref4.willUpdate,
      willUpdate = _ref4$willUpdate === void 0 ? function (i) {
    return i;
  } : _ref4$willUpdate;

  var _useState = useState(defaultState),
      state = _useState[0],
      setState = _useState[1];

  var lastValidTime = useRef(Date.now());

  var changeState = function changeState(nextState) {
    var returnedState = willUpdate(nextState);

    if (returnedState) {
      setState(returnedState);
    }
  };

  useEffect(function () {
    var cacheIsValid = !invalidatorTime || lastValidTime.current > invalidatorTime;
    lastValidTime.current = Date.now();
    var networkAnyway = cacheAndNetwork || !cacheIsValid;

    if (skipCall) {
      if (state.status === STATUSES.BUSY) {
        changeState(_extends({}, state, defaultState));
      }

      return undefined;
    }

    if (state.status !== STATUSES.BUSY) {
      changeState(_extends({}, state, {
        status: STATUSES.BUSY,
        dataMatchesRequest: false
      }));
    }

    var fromCache = retrieveCache({
      cacheType: cacheType,
      request: request,
      using: using
    });

    if (fromCache && fromCache.result) {
      changeState({
        data: fromCache.result,
        dataMatchesRequest: true,
        error: undefined,
        status: networkAnyway ? STATUSES.BUSY : STATUSES.DONE
      });
      return networkAnyway ? wrapPromise(fetchAndCache({
        cacheType: cacheType,
        request: request,
        using: using
      }), changeState) : undefined;
    }

    if (fromCache && fromCache.error) {
      changeState({
        data: undefined,
        dataMatchesRequest: false,
        error: errorToString(fromCache.error),
        status: networkAnyway ? STATUSES.BUSY : STATUSES.ERROR
      });
      return networkAnyway ? wrapPromise(fetchAndCache({
        cacheType: cacheType,
        request: request,
        using: using
      }), changeState) : undefined;
    }

    return wrapPromise(fromCache && cacheIsValid ? fromCache.promise : fetchAndCache({
      cacheType: cacheType,
      request: request,
      using: using
    }), changeState);
  }, [request, skipCall, toString(using), invalidatorTime]);
  return [state];
};

useFetch.STATUSES = STATUSES;
useFetch.CACHE_TYPES = CACHE_TYPES;
useFetch.STATE_RESET = _extends({}, defaultState, {
  data: undefined,
  error: undefined
});
export default useFetch;